import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";

export default class extends Controller {
  static targets = [
    "contractDistributionChart",
    "monthlyEvolutionChart"
  ];

  static values = {
    contractData: Array,
    monthlyData: Array
  }

  connect() {
    this.initContractDistributionChart();
    this.initMonthlyEvolutionChart();
  }

  disconnect() {
    if (this.contractChartInstance) this.contractChartInstance.destroy();
    if (this.monthlyChartInstance) this.monthlyChartInstance.destroy();
  }

  initContractDistributionChart() {
    if (!this.hasContractDistributionChartTarget) return;
    
    const ctx = this.contractDistributionChartTarget.getContext("2d");
    const contractData = this.contractDataValue || [];
    
    if (contractData.length === 0) return;
    
    this.contractChartInstance = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: contractData.map(item => item.name),
        datasets: [{
          data: contractData.map(item => item.count),
          backgroundColor: (window.g4sChartPalette ? window.g4sChartPalette(contractData.length) : undefined),
          borderColor: (window.g4sChartStroke ? window.g4sChartStroke() : "#fff"),
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = context.dataset.data.reduce((acc, data) => acc + data, 0);
                const percentage = Math.round((value * 100) / total);
                return `${label}: ${value} (${percentage}%)`;
              }
            }
          }
        },
        cutout: "70%"
      }
    });
  }

  initMonthlyEvolutionChart() {
    if (!this.hasMonthlyEvolutionChartTarget) return;
    
    const ctx = this.monthlyEvolutionChartTarget.getContext("2d");
    const monthlyData = this.monthlyDataValue || [];
    
    if (monthlyData.length === 0) return;
    
    this.monthlyChartInstance = new Chart(ctx, {
      type: "line",
      data: {
        labels: monthlyData.map(item => item.month).reverse(),
        datasets: (() => {
          // Palette tokenisée (--ds-chart-*), theme-aware. Le fill reprend la
          // couleur de série en version atténuée (hex + alpha "1a" ≈ 0.10).
          const palette = window.g4sChartPalette ? window.g4sChartPalette(3) : ["#3b82f6", "#f59e0b", "#29ac46"];
          const soft = (c) => (c && c.startsWith("#") && c.length === 7 ? c + "1a" : c);
          return ["salary_expenses", "salary_advances", "payments"].map((key, i) => ({
            label: this.t(`js.payroll_dashboard.chart.${key}`),
            data: monthlyData.map(item => item[key]).reverse(),
            borderColor: palette[i], backgroundColor: soft(palette[i]),
            borderWidth: 2, fill: true, tension: 0.4
          }));
        })()
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top"
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || "";
                if (label) {
                  label += ": ";
                }
                if (context.parsed.y !== null) {
                  label += new Intl.NumberFormat("fr-FR", { 
                    style: "currency", 
                    currency: "XOF", 
                    minimumFractionDigits: 0, 
                    maximumFractionDigits: 0 
                  }).format(context.parsed.y);
                }
                return label;
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return new Intl.NumberFormat("fr-FR", { 
                  style: "currency", 
                  currency: "XOF", 
                  minimumFractionDigits: 0, 
                  maximumFractionDigits: 0 
                }).format(value);
              }
            }
          }
        }
      }
    });
  }

  t(key, options = {}) {
    if (typeof window !== "undefined" && typeof window.t === "function") {
      return window.t(key, options);
    }
    // Fallback defaults
    const defaults = {
      'js.payroll_dashboard.chart.salary_expenses': 'Dépenses de salaires',
      'js.payroll_dashboard.chart.salary_advances': 'Avances sur salaire',
      'js.payroll_dashboard.chart.payments': 'Paiements'
    };
    return defaults[key] || key;
  }
}
